/**
 * The service corridor's working dog and handler.
 *
 * This animal is frightening because it is ordinary and competent, not because
 * it performs. Everything here is close, dry and small enough to belong in a
 * sealed concrete passage; there is deliberately no bark.
 */

import { connectChain, filter, gain, noiseSource } from '../synth';
import type { SoundDefinition } from '../types';

/** Schedule a short noise-shaped event without leaving a gain step at either end. */
function pulse(
  shape: GainNode,
  t0: number,
  start: number,
  attack: number,
  hold: number,
  release: number,
  level: number,
): void {
  shape.gain.setValueAtTime(0, t0 + start);
  shape.gain.linearRampToValueAtTime(level, t0 + start + attack);
  shape.gain.setValueAtTime(level, t0 + start + attack + hold);
  shape.gain.linearRampToValueAtTime(0, t0 + start + attack + hold + release);
}

/**
 * One second is two complete half-second pant cycles. Keeping both air paths at
 * zero at the wrap matters more than a longer variation here: this is the quiet
 * confirmation that the dog is still working, not a performance to notice.
 */
const dogPant: SoundDefinition = {
  id: 'dog-pant',
  label: 'Working dog pant',
  description: 'Close, quiet, unhurried two-cycle panting with a slightly brighter exhale.',
  category: 'creature',
  duration: 1,
  loop: true,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.42);
    connectChain(out, ctx.destination);

    const inhale = gain(ctx, 0);
    connectChain(
      noiseSource(ctx, 1, 401),
      filter(ctx, { type: 'bandpass', frequency: 680, q: 0.85 }),
      inhale,
      out,
    );

    const exhale = gain(ctx, 0);
    connectChain(
      noiseSource(ctx, 1, 409),
      // The exhale moves above the inhale's chestier band, but stops well below
      // a hiss that would turn a continuous bed into a conspicuous texture.
      filter(ctx, { type: 'bandpass', frequency: 1180, q: 0.95 }),
      exhale,
      out,
    );

    // Two exactly equal cycles at 2 Hz; no LFO is used, so every modulator and
    // every envelope also returns to its initial zero at the one-second seam.
    for (const offset of [0, 0.5] as const) {
      pulse(inhale, t0, offset, 0.018, 0.052, 0.07, 0.13);
      pulse(exhale, t0, offset + 0.145, 0.014, 0.061, 0.085, 0.16);
    }
  },
};

const dogClawsConcrete: SoundDefinition = {
  id: 'dog-claws-concrete',
  label: 'Dog claws on concrete',
  description: 'Three tight nail ticks over an almost absent hard-floor pad.',
  category: 'foley',
  duration: 0.14,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.38);
    connectChain(out, ctx.destination);

    // 12 ms then 15 ms spacing makes a paw's separate nails rather than one
    // synthetic click. The brief is quiet and distant-capable, so the third is
    // lighter instead of making the cluster louder as it progresses.
    const ticks: readonly [number, number, number][] = [
      [0.008, 0.12, 421],
      [0.02, 0.096, 431],
      [0.035, 0.07, 439],
    ];
    for (const [offset, level, seed] of ticks) {
      const tick = gain(ctx, 0);
      pulse(tick, t0, offset, 0.0015, 0.001, 0.011, level);
      connectChain(
        noiseSource(ctx, 0.14, seed),
        // Nothing below 2 kHz reaches the nails: a low component would make a
        // small dog sound like a boot, and would be wrong for sealed concrete.
        filter(ctx, { type: 'highpass', frequency: 2000, q: 0.7 }),
        filter(ctx, { type: 'lowpass', frequency: 7200, q: 0.7 }),
        tick,
        out,
      );
    }

    const pad = gain(ctx, 0);
    pulse(pad, t0, 0.01, 0.005, 0.009, 0.035, 0.024);
    connectChain(
      noiseSource(ctx, 0.14, 443),
      // Even the nearly inaudible pad is filtered above a footfall's body; the
      // landing is felt only as a little concrete contact, never a bass thump.
      filter(ctx, { type: 'highpass', frequency: 780, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 1500, q: 0.8 }),
      pad,
      out,
    );
  },
};

const dogSnuff: SoundDefinition = {
  id: 'dog-snuff',
  label: 'Dog scent cast',
  description: 'Three close, wet, uneven snuffs as a working dog reads concrete.',
  category: 'creature',
  duration: 0.9,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.43);
    connectChain(out, ctx.destination);

    // The 264 ms and 355 ms gaps refuse a human rhythm. Three snuffs make the
    // floor-reading action legible at distance without becoming a comic phrase.
    const snuffs: readonly [number, number, number][] = [
      [0.052, 0.17, 457],
      [0.316, 0.14, 463],
      [0.671, 0.19, 479],
    ];
    for (const [offset, level, seed] of snuffs) {
      const nose = gain(ctx, 0);
      pulse(nose, t0, offset, 0.004, 0.012, 0.072, level);
      connectChain(
        noiseSource(ctx, 0.9, seed),
        // This concentrated low-mid resonance is closer and wetter than panting;
        // it supplies a muzzle's small enclosed cavity without turning into voice.
        filter(ctx, { type: 'bandpass', frequency: 760, q: 1.7 }),
        nose,
        out,
      );

      const grit = gain(ctx, 0);
      pulse(grit, t0, offset + 0.004, 0.002, 0.007, 0.036, level * 0.3);
      connectChain(
        noiseSource(ctx, 0.9, seed + 1),
        // The narrow upper scrape is the concrete under a wet nose. It is kept
        // far below the body so it reads as grit, rather than as an aggressive snarl.
        filter(ctx, { type: 'bandpass', frequency: 2850, q: 1.15 }),
        grit,
        out,
      );
    }
  },
};

const handlerWhistle: SoundDefinition = {
  id: 'handler-whistle',
  label: 'Handler calling whistle',
  description: 'A breathy, descending two-note working whistle.',
  category: 'creature',
  duration: 0.55,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.39);
    connectChain(out, ctx.destination);

    // 1930 Hz to 1510 Hz descends, but neither note is chosen from a scale: the
    // call is a practical signal, not an in-farm musical motif. The second note
    // is deliberately 150 ms against the first's 205 ms.
    const notes: readonly [number, number, number, number][] = [
      [0.035, 0.205, 1930, 0.12],
      [0.315, 0.15, 1510, 0.105],
    ];
    const breath = gain(ctx, 0);
    connectChain(
      noiseSource(ctx, 0.55, 491),
      // A narrow peak carries the breath with the pitch, keeping a sine whistle
      // recognisably blown rather than making it sound like a clean UI tone.
      filter(ctx, { type: 'bandpass', frequency: 1760, q: 3.2 }),
      breath,
      out,
    );

    for (const [offset, length, frequency, level] of notes) {
      const tone = ctx.createOscillator();
      tone.type = 'sine';
      tone.frequency.value = frequency;
      const toneShape = gain(ctx, 0);
      pulse(toneShape, t0, offset, 0.012, length - 0.036, 0.024, level);
      connectChain(tone, toneShape, out);
      tone.start(0);

      // The same closed envelope keeps the blown component attached to each
      // note; its very small level is breath, not a second pitched instrument.
      pulse(breath, t0, offset, 0.012, length - 0.04, 0.028, 0.028);
    }
  },
};

export const dogSounds: readonly SoundDefinition[] = [
  dogPant,
  dogClawsConcrete,
  dogSnuff,
  handlerWhistle,
];
